/**
 * Company Service
 * 
 * Service for managing Company records in the ICFR system.
 * Handles all company-related API calls.
 */
import { apiRequest } from './apiClient';
import {
    Company,
} from '../services/companyService';
import { parseCommaSeparated } from '../utils/stringUtils';

export interface User {
  resetPasswordAllowed: boolean;
  id: string;
  email: string;
  userName: string;
  firstName: string;
  lastName: string;
  identityProvider: string;
  userRoles: string[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  phoneNumber: string | null;
  companyId: string;
  companyName: string | null;
  userType: string;
  invitedById: string | null;
  jobTitle: string | null;
  department: string | null;
  location: string | null;
  companies: Company[] | null;
  isPrimaryCompanyUser: boolean;
  disabledAt: string | null;
  requiresOnboarding: boolean;
  companyImageUrl: string | null;
  passwordChangeRequired: boolean;
}

interface UserBackend {
  ResetPasswordAllowed: boolean;
  Id: string;
  Email: string;
  UserName: string;
  FirstName: string;
  LastName: string;
  IdentityProvider: string;
  UserRoles: string[];
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
  PhoneNumber: string | null;
  CompanyId: string;
  CompanyName: string | null;
  UserType: string;
  InvitedById: string | null;
  JobTitle: string | null;
  Department: string | null;
  Location: string | null;
  CompanyIds: string | null;
  CompanyNames: string | null;
  IsPrimaryCompanyUser: boolean;
  DisabledAt: string | null;
  RequiresOnboarding: boolean;
  CompanyImageUrl: string | null;
  PasswordChangeRequired: boolean;
}

interface GetUsersBackendResponse {
    result: UserBackend[];
    count: number;
}

const mapBackendToUser = (backend: UserBackend): User => {
    const companyIds = parseCommaSeparated(backend.CompanyIds);
    const companyNames = parseCommaSeparated(backend.CompanyNames);

    const companies = companyIds.map((item, index) => {
        return {
            companyId: item,
            companyName: companyNames[index],
            companyDescription: "",
            internalAdminName: "",
            createdAt: ""
        };
    });

    return {
        resetPasswordAllowed: backend.ResetPasswordAllowed,
        id: backend.Id,
        email: backend.Email,
        userName: backend.UserName,
        firstName: backend.FirstName,
        lastName: backend.LastName,
        identityProvider: backend.IdentityProvider,
        userRoles: backend.UserRoles,
        createdAt: backend.CreatedAt,
        updatedAt: backend.UpdatedAt,
        deletedAt: backend.DeletedAt,
        phoneNumber: backend.PhoneNumber,
        companyId: backend.CompanyId,
        companyName: backend.CompanyName,
        userType: backend.UserType,
        invitedById: backend.InvitedById,
        jobTitle: backend.JobTitle,
        department: backend.Department,
        location: backend.Location,
        companies: companies,
        isPrimaryCompanyUser: backend.IsPrimaryCompanyUser,
        disabledAt: backend.DisabledAt,
        requiresOnboarding: backend.RequiresOnboarding,
        companyImageUrl: backend.CompanyImageUrl,
        passwordChangeRequired: backend.PasswordChangeRequired
    }
}

// ==================== API REQUEST/RESPONSE TYPES ====================
export interface GetUsersRequest {
    email?: string;
    userName?: string;
    companyName?: string;
    userType: string;
    skip?: number;
    take?: number;
    sorted?: { name: string; direction: 'ascending' | 'descending' }[];
}

export interface GetUsersResponse {
    data:User[];
    totalCount:number;
    lastFetchedUserType:string;
}

export interface UpdateUserRequest {
    firstName: string;
    lastName: string; 
    phoneNumber: string;
    jobTitle?: string | null;
    department?: string | null;
    location?: string | null;
    id: string;
    companyName?: string | null;
    companyIds?: string[] | null;
    isPrimaryCompanyUser: boolean;
}

export interface InviteUserRequest {
    firstName: string;
    lastName: string; 
    email: string;
    phoneNumber: string;
    userType: string;
    companyId?: string;
    companyIds? : string[];
    
}

export interface InviteUserResponse {
    id: string;
    email: string;
}

export interface DisableUserRequest {
    id: string;
}

export interface EnableUserRequest {
    id: string;
}

// ========== FILTER BUILDING UTILITIES ==========
// New Syncfusion filter structure from BE:
// - OR conditions: Use nested "predicates" array with "condition": "or"
// - AND conditions: Separate objects in "where" array (default)

type FilterValue = string | boolean | number | null;

interface FilterCondition {
  field: string;
  operator: string;
  value: FilterValue;
  condition?: 'and' | 'or';
}

interface FilterPredicateGroup {
  predicates: FilterCondition[];
}

type WhereCondition = FilterCondition | FilterPredicateGroup;

// ==================== API FUNCTIONS ====================

/**
 * Get all users for a given role
 * Default sort: Descending by GapDate (Newest first)
 * Note: Status filter is handled by backend by default
 * Optional processId/cycleId filters for navigation from Process Hierarchy
 */
export const getAllUsers = async (request: GetUsersRequest): Promise<GetUsersResponse> => {
  try {
    // Build where clause for filtering
    const {
      whereConditions,
      addSingleValueFilter,
    } = buildFilterConditions();
    
    addSingleValueFilter('Email', request.email);
    addSingleValueFilter('UserName', request.userName);
    addSingleValueFilter('CompanyName', request.companyName);


    const requestBody: {
          skip: number;
          take: number;
          requiresCounts: boolean;
          sorted?: { name: string; direction: 'ascending' | 'descending' }[];
          where?: WhereCondition[];
        } = {
          skip: request.skip || 0,
          take: request.take || 1000,
          requiresCounts: true,
        };
    
        // Only include sorted param if provided (let BE decide default)
        if (request.sorted) {
          requestBody.sorted = request.sorted;
        }
        
        // Add where conditions if any
        if (whereConditions.length > 0) {
          requestBody.where = whereConditions;
        }

    const response = await apiRequest<GetUsersBackendResponse, typeof requestBody>(
      'POST',
      `/admin/users/${request.userType}/index-grid`,
      requestBody,
      true // Authentication required
    );

    // Map backend data to frontend format
    let mappedData = response.result.map(mapBackendToUser);
    
    return {
      data: mappedData,
      totalCount: mappedData.length, // Update count to reflect filtered results
      lastFetchedUserType: request.userType
    };
  } catch (error) {
    console.error('Failed to fetch companies:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch companies',
    };
  }
};


export const updateUser = async (request: UpdateUserRequest): Promise<User> => {
  try {
    

    const requestBody = {
      firstName: request.firstName,
      lastName: request.lastName,
      phoneNumber: request.phoneNumber,
      jobTitle: request.jobTitle,
      department: request.department,
      location: request.location,
      companyName: request.companyName,
      companyIds: request.companyIds,
      isPrimaryCompanyUser: request.isPrimaryCompanyUser
    };

    const response = await apiRequest<UserBackend, typeof requestBody>(
      'PUT',
      `/admin/profile/${request.id}`,
      requestBody,
      true // Authentication required
    );

    // Map backend data to frontend format
    let mappedData = mapBackendToUser(response);
    
    return mappedData;// {
      //mappedData,
      //totalCount: mappedData.length, // Update count to reflect filtered results
    //};
  } catch (error) {
    console.error('Failed to update user:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update user',
    };
  }
};

export const inviteUser = async (request: InviteUserRequest): Promise<InviteUserResponse> => {
  try {
    

    const requestBody = {
      firstName: request.firstName,
      lastName: request.lastName,
      phoneNumber: request.phoneNumber,
      email: request.email,
      companyId: request.companyId,
      companyIds: request.companyIds,
    };

    const response = await apiRequest<InviteUserResponse, typeof requestBody>(
      'POST',
      `/admin/account/${request.userType}/create`,
      requestBody,
      true // Authentication required
    );

    // Map backend data to frontend format
    //let mappedData = mapBackendToUser(response);
    
    return response;// {
      //mappedData,
      //totalCount: mappedData.length, // Update count to reflect filtered results
    //};
  } catch (error) {
    console.error('Failed to create and invite user:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create and invite user',
    };
  }
};


export const disableUser = async (request: DisableUserRequest): Promise<User> => {
  try {
    const requestBody = {
      id: request.id
    };

    const response = await apiRequest<UserBackend, typeof requestBody>(
      'POST',
      `/identity-user-management/users/${request.id}/deactivate`,
      requestBody,
      true // Authentication required
    );

    // Map backend data to frontend format
    let mappedData = mapBackendToUser(response);
    
    return mappedData;// {
      //mappedData,
      //totalCount: mappedData.length, // Update count to reflect filtered results
    //};
  } catch (error) {
    console.error('Failed to disable user:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to disable user',
    };
  }
};

export const reenableUser = async (request: EnableUserRequest): Promise<User> => {
  try {
    const requestBody = {
      id: request.id
    };

    const response = await apiRequest<UserBackend, typeof requestBody>(
      'POST',
      `/identity-user-management/users/${request.id}/activate`,
      requestBody,
      true // Authentication required
    );

    // Map backend data to frontend format
    let mappedData = mapBackendToUser(response);
    
    return mappedData;// {
      //mappedData,
      //totalCount: mappedData.length, // Update count to reflect filtered results
    //};
  } catch (error) {
    console.error('Failed to disable user:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to disable user',
    };
  }
};


/**
 * Build filter conditions for Syncfusion grid API
 * - Multiple values for same field: grouped with OR using predicates
 * - Single values: direct filter condition
 * - Different fields: AND logic (default)
 */
const buildFilterConditions = () => {
  const whereConditions: WhereCondition[] = [];

  // Add filter for multiple string values (OR within same field)
  const addMultiValueFilter = (field: string, values: string[] | undefined) => {
    if (!values?.length) return;
    
    if (values.length === 1) {
      // Single value - direct condition
      whereConditions.push({
        field,
        operator: 'equal',
        value: values[0],
      });
    } else {
      // Multiple values - use predicates with OR
      whereConditions.push({
        predicates: values.map(value => ({
          field,
          operator: 'equal',
          value,
          condition: 'or' as const,
        })),
      });
    }
  };

  // Add filter for multiple numeric values using Raw mapping (OR within same field)
  const addRawMultiValueFilter = (field: string, values: string[] | undefined, toRawFn: (v: string) => number) => {
    if (!values?.length) return;
    
    if (values.length === 1) {
      // Single value - direct condition
      whereConditions.push({
        field,
        operator: 'equal',
        value: toRawFn(values[0]),
      });
    } else {
      // Multiple values - use predicates with OR
      whereConditions.push({
        predicates: values.map(value => ({
          field,
          operator: 'equal',
          value: toRawFn(value),
          condition: 'or' as const,
        })),
      });
    }
  };

  // Add filter for single boolean/value
  const addSingleValueFilter = (field: string, value: FilterValue | undefined) => {
    if (value === null || value === undefined) return;
    
    whereConditions.push({
      field,
      operator: 'contains',
      value,
    });
  };

  // Add filter for boolean array (OR within same field) - for Design/Operating Effectiveness
  const addBooleanArrayFilter = (field: string, values: (boolean | null)[] | undefined) => {
    if (!values?.length) return;
    
    // Filter out null values (handled by frontend)
    const booleanValues = values ?? [];
    if (!booleanValues.length) return;
    
    if (booleanValues.length === 1) {
      whereConditions.push({
        field,
        operator: 'equal',
        value: booleanValues[0],
      });
    } else {
      whereConditions.push({
        predicates: booleanValues.map(value => ({
          field,
          operator: 'equal',
          value,
          condition: 'or' as const,
        })),
      });
    }
  };

  return {
    whereConditions,
    addMultiValueFilter,
    addRawMultiValueFilter,
    addSingleValueFilter,
    addBooleanArrayFilter,
  };
};